import React, { useState } from 'react';
import axios from 'axios';
import { Mail, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/signup`, { email, name: name || undefined });
      toast.success('Successfully subscribed! Check your inbox.');
      setEmail('');
      setName('');
      onClose();
    } catch (error) {
      if (error.response?.data?.message === 'Already subscribed') {
        toast.info('You\'re already subscribed!');
        onClose();
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="newsletter-modal"
    >
      <div className="bg-white dark:bg-[#2D2A26] rounded-xl border-2 border-black dark:border-white hard-shadow-lg max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          data-testid="close-newsletter-modal"
          className="absolute top-3 right-3 p-2 hover:bg-[#F0F0F0] dark:hover:bg-[#3D3A36] rounded-full transition-all"
        >
          <X className="w-5 h-5 dark:text-white" strokeWidth={2.5} />
        </button>

        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
            <h2 className="text-2xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Never Miss the Wit
            </h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Get daily witty news summaries delivered to your inbox at 7am EST
          </p>
        </div>

        <form onSubmit={handleSubscribe} className="space-y-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            data-testid="modal-newsletter-name"
            className="w-full border-2 border-black dark:border-white rounded-lg p-3 text-black dark:text-white dark:bg-[#3D3A36] focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="modal-newsletter-email"
            required
            className="w-full border-2 border-black dark:border-white rounded-lg p-3 text-black dark:text-white dark:bg-[#3D3A36] focus:ring-2 focus:ring-[#FF6B6B] focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="modal-newsletter-submit"
            className="w-full bg-[#FF6B6B] text-white font-bold px-6 py-3 rounded-lg border-2 border-black hover:bg-[#FF5252] transition-all hard-shadow-hover flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Mail className="w-5 h-5" strokeWidth={2.5} />
            {loading ? 'Subscribing...' : 'Subscribe Free'}
          </button>
        </form>

        <p className="text-xs text-gray-500 dark:text-gray-500 mt-4 text-center">
          No spam. Unsubscribe anytime. Just pure wit.
        </p>
      </div>
    </div>
  );
};

export default NewsletterModal;
